import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { FiMessageCircle, FiX } from 'react-icons/fi';

import { MetaTags } from '@components';
import {
  useBroadcastQuery,
  useBroadcastChannels,
  useFloatingPlayer,
} from '@hooks';
import { RoomBroadcastData } from '@common/types/api';

import {
  BroadcastTitle,
  VideoPlayer,
  InfoTabs,
  InteractionPanel,
  MobileBottomBar,
  MobileDrawer,
} from './components';
import { BroadcastProvider, useBroadcast } from './context/BroadcastContext';
import CallToAction from './CallToAction';
import BroadcastSponsors from './BroadcastSponsors';
import BroadcastBanners from './BroadcastBanners';
import Emoji from './Streaming/Emoji';
import { TABS } from './Interactions/constants';
import * as S from './styles';

// Hook para gerenciar o floating player
const useFloatingPlayerSync = (
  broadcastData: RoomBroadcastData | undefined,
  currentLanguageIndex: number,
) => {
  const { isVisible, updateFloatingPlayer } = useFloatingPlayer();

  // Ref para armazenar os dados mais recentes do player
  const playerDataRef = useRef<{
    src?: string;
    roomName?: string;
    streamingMethod?: number;
  }>({});

  // Atualiza a ref sempre que broadcastData mudar
  useEffect(() => {
    const streaming = broadcastData?.streamings.data[currentLanguageIndex];
    playerDataRef.current = {
      src: streaming?.url_video,
      roomName: broadcastData?.name,
      streamingMethod: streaming?.streaming_method,
    };
  }, [broadcastData, currentLanguageIndex]);

  // Esconde o floating player ao entrar na página de broadcast (apenas uma vez)
  useEffect(() => {
    if (isVisible) {
      updateFloatingPlayer({ isVisible: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mostra o floating player apenas ao sair da página (unmount real)
  useEffect(
    () => () => {
      const { src, roomName, streamingMethod } = playerDataRef.current;
      if (src && roomName) {
        updateFloatingPlayer({
          src,
          roomName,
          streamingMethod,
          isVisible: true,
        });
      }
    },
    [updateFloatingPlayer],
  );
};

// eslint-disable-next-line radar/cognitive-complexity
const BroadcastContent = () => {
  const broadcastQuery = useBroadcastQuery();
  const { t } = useTranslation();
  const {
    theaterMode,
    sidebarCollapsed,
    toggleSidebar,
    mobileDrawerOpen,
    openMobileDrawer,
    closeMobileDrawer,
    activeTab,
    setActiveTab,
  } = useBroadcast();

  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);
  const [theaterSidebarOpen, setTheaterSidebarOpen] = useState(false);

  // Inicializa com o streaming_main quando os dados carregam
  useEffect(() => {
    const streamings = broadcastQuery.data?.streamings.data;
    if (!streamings?.length) return;

    const mainIndex = streamings.findIndex(
      (streaming) => !!streaming.streaming_main,
    );

    if (mainIndex >= 0) {
      setCurrentLanguageIndex(mainIndex);
    }
  }, [broadcastQuery.data?.streamings.data]);

  const toggleTheaterSidebar = useCallback(() => {
    setTheaterSidebarOpen((prev) => !prev);
  }, []);

  useBroadcastChannels(broadcastQuery.data?.id);
  useFloatingPlayerSync(broadcastQuery.data, currentLanguageIndex);

  const settings = broadcastQuery.data?.settings.data;

  // CTA, Sponsors e Banners são mutuamente exclusivos
  const showCTA = !!settings?.call_action;
  const showSponsors = !showCTA && !!settings?.sponsors;
  const showBanners = !showCTA && !showSponsors && !!settings?.banners;

  const getTitleExtra = () => {
    if (showCTA) return <CallToAction />;
    if (showSponsors) return <BroadcastSponsors />;
    if (showBanners) return <BroadcastBanners />;
    return null;
  };
  const titleExtra = getTitleExtra();

  // Get visible tabs for mobile bottom bar from settings
  const mobileVisibleTabs = useMemo(() => {
    if (!settings?.visible_interactions) {
      return TABS;
    }
    return settings.visible_interactions
      .filter((interaction) => interaction.visible)
      .sort((a, b) => a.order - b.order)
      .map((interaction) => TABS.find((tab) => tab.id === interaction.key))
      .filter(Boolean) as typeof TABS;
  }, [settings]);

  const streamings = broadcastQuery.data?.streamings.data;
  const currentStreaming = streamings?.[currentLanguageIndex];
  const pageTitle = broadcastQuery.data?.name
    ? `${t('pages.broadcast.title')} "${broadcastQuery.data.name}"`
    : t('pages.broadcast.titleFall');

  return (
    <S.Container $theaterMode={theaterMode}>
      <MetaTags title={pageTitle} />

      <S.MainLayout
        $theaterMode={theaterMode}
        $sidebarCollapsed={sidebarCollapsed}
      >
        <S.PlayerSection $theaterMode={theaterMode}>
          {!theaterMode && (
            <BroadcastTitle
              title={broadcastQuery.data?.name}
              startDate={broadcastQuery.data?.start_date}
              endDate={broadcastQuery.data?.end_date}
              extra={titleExtra}
            />
          )}

          <S.PlayerWrapper>
            <VideoPlayer
              src={currentStreaming?.url_video}
              thumb={broadcastQuery.data?.thumb_image}
              isLive
              streamings={streamings}
              currentLanguageIndex={currentLanguageIndex}
              onLanguageChange={setCurrentLanguageIndex}
            />
            {theaterMode && (
              <S.TheaterInteractionButton
                type="button"
                onClick={toggleTheaterSidebar}
                $active={theaterSidebarOpen}
              >
                {theaterSidebarOpen ? <FiX /> : <FiMessageCircle />}
              </S.TheaterInteractionButton>
            )}
            <Emoji />
          </S.PlayerWrapper>

          {!theaterMode && <InfoTabs />}
        </S.PlayerSection>

        {theaterMode && (
          <S.TheaterSidebar $open={theaterSidebarOpen}>
            <InteractionPanel settings={settings} onlineCount={1247} inline />
          </S.TheaterSidebar>
        )}
      </S.MainLayout>

      {!theaterMode && (
        <InteractionPanel
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          onlineCount={1247}
          settings={settings}
        />
      )}

      {!theaterMode && (
        <MobileBottomBar
          activeTab={activeTab}
          onTabClick={openMobileDrawer}
          visibleTabs={mobileVisibleTabs}
        />
      )}

      <MobileDrawer
        isOpen={mobileDrawerOpen}
        onClose={closeMobileDrawer}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        visibleTabs={mobileVisibleTabs}
      >
        <InteractionPanel settings={settings} onlineCount={1247} inline />
      </MobileDrawer>
    </S.Container>
  );
};

const Broadcast = () => (
  <BroadcastProvider>
    <BroadcastContent />
  </BroadcastProvider>
);

export default Broadcast;
